import type { ContentTranslations } from '../i18n/content';
import type { Language } from '../i18n';
import api from './api';
import { PortfolioItem } from '../context/PortfolioContext';

// Interface to match the backend model
export interface ProjectData {
  sourceLanguage?: Language;
  translations?: ContentTranslations;
  id?: number;
  title: string;
  slug: string;
  description: string;
  content: string;
  image?: string;
  category: string;
  date: string;
  url?: string;
  github?: string;
  technologies: string[];
  featured: boolean;
  gallery?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

// Function to map backend data to frontend format
const mapToFrontendProject = (project: ProjectData): PortfolioItem => {
  return {
    id: project.id?.toString() || '',
    contentLanguage: project.sourceLanguage,
    sourceLanguage: project.sourceLanguage,
    translations: project.translations,
    title: project.title,
    slug: project.slug,
    description: project.description,
    longDescription: project.content,
    image: project.image || '',
    technologies: project.technologies || [],
    category: project.category || 'Web Development',
    date: project.date || (project.createdAt ? new Date(project.createdAt).toISOString().split('T')[0] : ''),
    url: project.url,
    github: project.github,
    featured: project.featured,
    gallery: project.gallery || []
  };
};

// Function to map frontend data to backend format
const mapToBackendProject = (project: Partial<PortfolioItem>): Partial<ProjectData> => {
  return {
    sourceLanguage: project.sourceLanguage,
    translations: project.translations,
    title: project.title,
    slug: project.slug,
    description: project.description || '',
    content: project.longDescription || '',
    image: project.image,
    category: project.category || 'Web Development',
    date: project.date || new Date().toISOString().split('T')[0],
    url: project.url,
    github: project.github,
    technologies: project.technologies || [],
    featured: project.featured || false,
    gallery: project.gallery || []
  };
};

// Get all projects
export const getAllProjects = async (includeAdmin = false): Promise<PortfolioItem[]> => {
  try {
    const response = await api.get(includeAdmin ? '/projects/admin/all' : '/projects');
    return response.data.map(mapToFrontendProject);
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

// Get a project by slug
export const getProjectBySlug = async (slug: string): Promise<PortfolioItem> => {
  try {
    const response = await api.get(`/projects/${slug}`);
    return mapToFrontendProject(response.data);
  } catch (error) {
    console.error(`Error fetching project with slug ${slug}:`, error);
    throw error;
  }
};

// Create a new project
export const createProject = async (project: Omit<PortfolioItem, 'id'>): Promise<PortfolioItem> => {
  try {
    const response = await api.post('/projects', mapToBackendProject(project));
    return mapToFrontendProject(response.data);
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Update a project
export const updateProject = async (id: string, project: Partial<PortfolioItem>): Promise<PortfolioItem> => {
  try {
    const response = await api.put(`/projects/${id}`, mapToBackendProject(project));
    return mapToFrontendProject(response.data);
  } catch (error) {
    console.error(`Error updating project with id ${id}:`, error);
    throw error;
  }
};

// Delete a project
export const deleteProject = async (id: string): Promise<void> => {
  try {
    await api.delete(`/projects/${id}`);
  } catch (error) {
    console.error(`Error deleting project with id ${id}:`, error);
    throw error;
  }
};
